import React, {useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {useShallow} from 'zustand/react/shallow';
import {Box, styled} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import Header from '@contentlab/components/header';
import Menu from '@contentlab/components/menu';
import {ROUTES} from '@contentlab/config/routesConstants';
import useScrollPosition from '@contentlab/hooks/use-scroll-position';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {media} from '@contentlab/styles/mediaQueries';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import {DashboardUser} from '@/types/dashboardUser';
import useIsMobile from '@/hooks/use-is-mobile';

interface LayoutProps {
  testID?: string;
  user: DashboardUser;
  children: React.ReactNode;
}

const pagesWithSpecialBackground = [
  `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_REPHRASE}`,
  `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_DUB}`,
  `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_RESIZE}`,
  `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_CAPTIONS}`,
  `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_AI_AVATAR}`,
  `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECT}/`, // Adding the slash for not including the projects page
];

const shouldUseSecondaryBackground = (pathname: string) => {
  return pagesWithSpecialBackground.some((page) => pathname.includes(page));
};

type PagePadding = {
  vertical: string;
  horizontal: string;
};

const DEFAULT_PADDING = {
  vertical: '24px',
  horizontal: '32px',
};

const pagesWithSpecialPadding: Record<string, PagePadding> = {
  [ROUTES.CONTENT_LAB]: {
    vertical: '16px',
    horizontal: '48px',
  },
  [`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECTS}`]: {
    vertical: '0px',
    horizontal: '0px',
  },
};

const Root = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  background: designSystemToken('semantic.bg.tertiary'),
  [media.mobile]: {
    flexDirection: 'column-reverse',
    height: '100vh',
  },
}));

const Content = styled(Box)(() => ({
  boxSizing: 'border-box',
  flex: 1,
  height: '100vh',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '32px 0 0 32px',
  [media.mobile]: {
    borderTopLeftRadius: '0',
    borderBottomLeftRadius: '0',
    height: '100%',
  },
}));

const ContentInner = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
}));

const ScrollableContent = styled(Box)(() => ({
  boxSizing: 'border-box',
  flex: 1,
  overflow: 'auto',
  width: '100%',
  ...scrollbarMixins.overrideScrollbar(),
}));

function Layout({testID = 'layout', user, children}: LayoutProps) {
  const location = useLocation();
  const {menuOpen} = contentLabStore(
    useShallow((state) => ({
      menuOpen: state.menuOpen,
    }))
  );

  const scrollRef = useScrollPosition<HTMLDivElement>(location.pathname);

  const isMobile = useIsMobile();
  const proxyOpen = isMobile ? false : menuOpen; // Always closed on mobile

  const backgroundColor = useMemo(() => {
    return shouldUseSecondaryBackground(location.pathname)
      ? designSystemToken('semantic.bg.primary')
      : designSystemToken('semantic.bg.secondary');
  }, [location.pathname]);

  const padding = useMemo(() => {
    return pagesWithSpecialPadding[location.pathname] || DEFAULT_PADDING;
  }, [location.pathname]);

  return (
    <Root data-testid={testID}>
      <Menu
        user={user}
        open={proxyOpen}
        isMobile={isMobile}
        onToggle={() => contentLabActions.setMenuOpen(!menuOpen)}
      />
      <Content>
        <ContentInner bgcolor={backgroundColor}>
          <Header />
          <ScrollableContent
            ref={scrollRef}
            sx={{
              paddingY: padding.vertical,
              paddingX: padding.horizontal,
            }}
          >
            {children}
          </ScrollableContent>
        </ContentInner>
      </Content>
    </Root>
  );
}

export default Layout;
